import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl} from 'react-intl';

import DomiExamplesComponent from '../components/domi-examples/domi-examples.jsx';
import hardwareExampleDevices from '../lib/libraries/hardware-example-devices.js';
import intlShape from '../lib/intlShape.js';
import {clearCloudProjectKey} from '../lib/domi-cloud-project.js';
import log from '../lib/log.js';
import {closeDomiExamples, closeLoadingProject, openLoadingProject} from '../reducers/modals.js';
import {activateTab, BLOCKS_TAB_INDEX} from '../reducers/editor-tab.js';
import {setProjectChanged} from '../reducers/project-changed.js';
import {setProjectTitle} from '../reducers/project-title.js';

const messages = defineMessages({
    replaceWarning: {
        id: 'gui.domiExamples.replaceWarning',
        defaultMessage: 'Loading this example will replace the current workspace. Continue?',
        description: 'Warning before replacing the current project with an example'
    },
    loadError: {
        id: 'gui.domiExamples.loadError',
        defaultMessage: 'The example could not be loaded. Please try again.',
        description: 'Error shown when a Domi example project cannot be loaded'
    }
});

const DomiExamples = ({
    intl,
    onActivateBlocksTab,
    onLoadingFinished,
    onLoadingStarted,
    onProjectLoaded,
    onRequestClose,
    onSetProjectTitle,
    projectChanged,
    vm
}) => {
    const [loadingId, setLoadingId] = useState(null);
    const [error, setError] = useState('');

    const handleLoadExample = useCallback(async example => {
        const shouldReplace = !projectChanged ||
            window.confirm(intl.formatMessage(messages.replaceWarning)); // eslint-disable-line no-alert
        if (!shouldReplace) return;

        setLoadingId(example.id);
        setError('');
        onLoadingStarted();
        vm.stopAll();
        try {
            const device = hardwareExampleDevices.find(item => item.examples.includes(example));
            const extensionId = device && device.extensionId;
            const extensionIds = example.extensionIds || (extensionId ? [extensionId] : []);
            const extensionManager = vm.extensionManager;
            if (extensionManager) {
                for (const requiredExtensionId of extensionIds) {
                    if (!extensionManager.isExtensionLoaded(requiredExtensionId)) {
                        await extensionManager.loadExtensionURL(requiredExtensionId);
                    } else if (extensionManager.refreshBlocks) {
                        // Examples may contain blocks added since this extension was first registered.
                        // Refresh their Blockly definitions before workspace XML is restored.
                        await extensionManager.refreshBlocks();
                    }
                }
            }
            const projectUrl = new URL(example.projectPath, document.baseURI);
            if (example.assetVersion) projectUrl.searchParams.set('v', example.assetVersion);
            const response = await fetch(projectUrl.toString(), {cache: 'no-store'});
            if (!response.ok) throw new Error(`Example request failed (${response.status})`);
            await vm.loadProject(await response.arrayBuffer());
            clearCloudProjectKey();
            onSetProjectTitle(intl.formatMessage(example.title));
            onProjectLoaded();
            onActivateBlocksTab();
            onRequestClose();
        } catch (loadError) {
            log.error('Failed to load Domi example', loadError);
            setError(intl.formatMessage(messages.loadError));
        } finally {
            onLoadingFinished();
            setLoadingId(null);
        }
    }, [
        intl,
        onActivateBlocksTab,
        onLoadingFinished,
        onLoadingStarted,
        onProjectLoaded,
        onRequestClose,
        onSetProjectTitle,
        projectChanged,
        vm
    ]);

    return (
        <DomiExamplesComponent
            error={error}
            devices={hardwareExampleDevices}
            loadingId={loadingId}
            onLoadExample={handleLoadExample}
            onRequestClose={onRequestClose}
        />
    );
};

DomiExamples.propTypes = {
    intl: intlShape.isRequired,
    onActivateBlocksTab: PropTypes.func.isRequired,
    onLoadingFinished: PropTypes.func.isRequired,
    onLoadingStarted: PropTypes.func.isRequired,
    onProjectLoaded: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSetProjectTitle: PropTypes.func.isRequired,
    projectChanged: PropTypes.bool,
    vm: PropTypes.shape({
        extensionManager: PropTypes.shape({
            isExtensionLoaded: PropTypes.func.isRequired,
            loadExtensionURL: PropTypes.func.isRequired,
            refreshBlocks: PropTypes.func
        }),
        loadProject: PropTypes.func.isRequired,
        stopAll: PropTypes.func.isRequired
    }).isRequired
};

const mapStateToProps = state => ({
    projectChanged: state.scratchGui.projectChanged,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onActivateBlocksTab: () => dispatch(activateTab(BLOCKS_TAB_INDEX)),
    onLoadingFinished: () => dispatch(closeLoadingProject()),
    onLoadingStarted: () => dispatch(openLoadingProject()),
    onProjectLoaded: () => dispatch(setProjectChanged()),
    onRequestClose: () => dispatch(closeDomiExamples()),
    onSetProjectTitle: title => dispatch(setProjectTitle(title))
});

export {DomiExamples};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DomiExamples));
